import { ReactElement, useMemo } from 'react';
import styles from './user-map-marker.module.scss';
import { Layer, Marker, Source } from 'react-map-gl/mapbox';
import { UIText } from '@/ui';
import { USER_ZINDEX } from '../util/map-helpers';
import circle from '@turf/circle';
import { blue } from '@/ui/styles';
import { uuidv4 } from '@/common/util';
import { useTasksTranslation } from '../hooks';

const IMAGE_SIZE = 60;
const IMAGE_SHIFT = 20;
const USER_DISPLAY_LIMIT = 4;

export type MapUserLocation = {
    userId?: string;
    taskId: string;
    image: string;
    lat: number;
    lng: number;
    acc?: number | null;
};

export type UserMapMarkerProps = {
    lat: number;
    lng: number;
    users: MapUserLocation[];
    opacity?: number;
};

export function UserMapMarker(props: UserMapMarkerProps): ReactElement {
    const { lat, lng, users, opacity } = props;
    const { t } = useTasksTranslation();

    const userCount = users.length;
    const displayUsers = useMemo(() => {
        return users.length > USER_DISPLAY_LIMIT
            ? users.slice(0, USER_DISPLAY_LIMIT)
            : users;
    }, [users]);

    const markerWidth = useMemo(() => {
        return (
            IMAGE_SIZE +
            (Math.min(userCount, USER_DISPLAY_LIMIT) - 1) * IMAGE_SHIFT
        );
    }, [userCount]);

    const radiusCircleData = useMemo(() => {
        if (users.length !== 1) {
            return null;
        }
        const acc = users[0].acc;
        if (!acc) {
            return null;
        }

        return circle([lng, lat], acc, {
            steps: 100,
            units: 'meters'
        });
    }, [lat, lng, users]);

    const radiusSourceId = useMemo(() => {
        return uuidv4();
    }, []);

    return (
        <>
            <Marker
                longitude={lng}
                latitude={lat}
                anchor={'bottom'}
                style={{ zIndex: USER_ZINDEX }}
            >
                <div className={styles.markerContainer} style={{ opacity }}>
                    <div
                        className={styles.usersContainer}
                        style={{ width: markerWidth }}
                    >
                        {displayUsers.map((user, index) => (
                            <div
                                key={user.taskId}
                                className={styles.contentContainer}
                                style={{
                                    transform: `translateX(${index * IMAGE_SHIFT}px)`,
                                    zIndex: userCount - index
                                }}
                            >
                                <img
                                    src={user.image}
                                    className={styles.image}
                                    alt={t('shared.labels.user')}
                                />
                            </div>
                        ))}
                        {userCount > USER_DISPLAY_LIMIT && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -4,
                                    paddingLeft: 8,
                                    paddingTop: 2,
                                    paddingRight: 8,
                                    paddingBottom: 2,
                                    backgroundColor: 'white',
                                    borderRadius: 999,

                                    zIndex: 999
                                }}
                            >
                                <UIText variant={'small'} medium>
                                    {t(
                                        'components.user_map_marker.overflow_count',
                                        {
                                            value:
                                                userCount - USER_DISPLAY_LIMIT
                                        }
                                    )}
                                </UIText>
                            </div>
                        )}
                    </div>

                    <div className={styles.arrow} />
                </div>
            </Marker>
            {radiusCircleData && (
                <Source
                    id={`circleSource-${radiusSourceId}`}
                    type="geojson"
                    data={radiusCircleData}
                >
                    <Layer
                        id={`circleLayer-${radiusSourceId}`}
                        type="fill"
                        paint={{
                            'fill-color': blue,
                            'fill-opacity': 0.3
                        }}
                    />
                </Source>
            )}
        </>
    );
}
